import type { PlanListingResponse } from './api/types';
import { useI18n } from './i18n/context';
import { intlTagFor } from './i18n/locale';
import { OpenPlanForm } from './OpenPlanForm';

/** A plan the caller just deleted — restorable from the home screen (ADR-027 §3 soft delete). */
export interface DeletedNotice {
  id: string;
  name: string;
}

/**
 * The signed-in landing screen (ADR-027 §5): the caller's plans — owned or shared with
 * them — newest first, above the create / open-by-id gestures. A plan deleted a moment ago
 * can be restored from here within the retention window.
 */
export function HomeScreen({
  rows,
  busy,
  deleted,
  scope = 'all',
  onOpen,
  onCreate,
  onRestore,
  onRefresh,
}: {
  /** The listing, or null while it loads. */
  rows: readonly PlanListingResponse[] | null;
  busy: boolean;
  deleted: DeletedNotice | null;
  /** The shell filters shared rows before passing them to this view. */
  scope?: 'all' | 'shared';
  onOpen: (planId: string, name: string | null) => void;
  onCreate: (name: string) => void;
  onRestore: (planId: string) => void;
  onRefresh: () => void;
}) {
  const { locale, messages: t } = useI18n();
  const g = t.grove;
  const copy = {
    all: {
      eyebrow: g.homeEyebrow,
      title: t.home.heading,
      description: g.homeDescription,
      loading: t.home.loading,
      empty: t.home.empty,
    },
    shared: {
      eyebrow: g.sharedEyebrow,
      title: g.sharedHeading,
      description: g.sharedDescription,
      loading: g.sharedLoading,
      empty: g.sharedEmpty,
    },
  }[scope];
  return (
    <div className="home-screen">
      {deleted !== null && (
        <p className="notice" role="status">
          {t.home.deleted(deleted.name)}{' '}
          <button className="button-quiet" disabled={busy} onClick={() => onRestore(deleted.id)}>
            {t.home.undo}
          </button>
        </p>
      )}
      <section aria-labelledby="my-plans">
        <div className="page-heading">
          <div>
            <p className="eyebrow">{copy.eyebrow}</p>
            <h2 id="my-plans">{copy.title}</h2>
            <p className="muted">{copy.description}</p>
          </div>
          <button className="button-secondary" disabled={busy || rows === null} onClick={onRefresh}>
            {t.home.refresh}
          </button>
        </div>
        {rows === null && <p className="empty-state">{copy.loading}</p>}
        {rows !== null && rows.length === 0 && <p className="empty-state">{copy.empty}</p>}
        {rows !== null && rows.length > 0 && (
          <ul className="plan-grid">
            {rows.map((row) => (
              <li className="plan-card" key={row.id}>
                <button
                  className="plan-card-open"
                  aria-label={row.name}
                  disabled={busy}
                  onClick={() => onOpen(row.id, row.name)}
                >
                  <span className="plan-card-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <rect x="4" y="3" width="16" height="18" rx="3" />
                      <path d="M8 8h8M8 12h8M8 16h5" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="plan-card-title">{row.name}</span>
                  <span className="plan-card-arrow" aria-hidden="true">
                    ↗
                  </span>
                </button>
                <div className="plan-card-meta">
                  <span
                    className="access-badge"
                    data-access={row.role}
                    aria-label={t.home.roleFor(row.name)}
                  >
                    {t.role[row.role]}
                  </span>
                  <time dateTime={row.updatedAt}>
                    {new Date(row.updatedAt).toLocaleString(intlTagFor(locale))}
                  </time>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      {scope === 'all' && (
        <OpenPlanForm busy={busy} onCreate={onCreate} onOpen={(planId) => onOpen(planId, null)} />
      )}
    </div>
  );
}
